import React, { useState, useMemo } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Checkbox,
	Chip,
	FormControlLabel,
	Stack,
	Typography
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import SearchField from '../../../components/ui/SearchField';

/**
 * Un permiso es de impresion si su clave arranca con "PRINT ". Se decide por la
 * clave y no por la etiqueta porque la clave es lo que viaja a la base y no
 * cambia si alguien reescribe el texto que se muestra.
 */
const esImpresion = (p) => p.description.startsWith('PRINT ');

/**
 * Selector de permisos agrupado por modulo.
 *
 * Portado del refactor hecho en la rama CrediHouse. Antes los permisos se
 * pintaban todos a la vez, cada uno con un useState propio y la etiqueta ENCIMA
 * del checkbox: sin alineacion entre filas, sin forma de buscar uno concreto y
 * sin saber cuantos llevaba marcados el rol.
 *
 * Los cambios de fondo son cuatro:
 *
 *   - Buscador. Filtra por etiqueta y tambien por la clave interna, que es como
 *     estan escritos los permisos en la base ("CREDITS UPDATE").
 *   - Modulos colapsables, cerrados de entrada.
 *   - Contador y seleccionar-todo por modulo, con estado indeterminado cuando
 *     va a medias.
 *   - Grilla de ancho fijo en lugar de fila que envuelve, para que los
 *     checkboxes queden en columnas parejas.
 *
 * Los checkboxes muestran SIEMPRE lo que el rol tiene guardado, incluso con
 * "acceso total" activado. Antes esa opcion los pintaba todos marcados y
 * bloqueados, lo que ocultaba el estado real: un rol podia figurar con 50 de 50
 * permisos y en la base tener 47, sin manera de ver cuales le faltaban. El
 * switch de acceso total sigue marcandolos todos de una vez, pero como accion,
 * no como un filtro visual encima de los datos.
 */
const PermissionsPicker = ({
	modules,
	permissions,
	onToggle,
	onToggleMany,
	readOnly = false
}) => {
	const bloqueado = readOnly;
	const [searchTerm, setSearchTerm] = useState('');
	const [expanded, setExpanded] = useState(false);

	const term = searchTerm.trim().toUpperCase();

	const parsed = useMemo(() => {
		const coincide = (p) =>
			!term ||
			p.label.toUpperCase().includes(term) ||
			p.description.toUpperCase().includes(term);

		return Object.entries(modules)
			.map(([modName, perms]) => {
				const marcado = (p) => !!permissions[p.description];
				const visibles = perms.filter(coincide);
				return {
					modName,
					// Los de impresion van aparte: son permisos sobre DOCUMENTOS, no
					// sobre operaciones.
					operaciones: visibles.filter((p) => !esImpresion(p)),
					impresion: visibles.filter(esImpresion),
					gobernables: perms,
					total: perms.length,
					marcados: perms.filter(marcado).length
				};
			})
			.filter((m) => m.operaciones.length > 0 || m.impresion.length > 0);
	}, [modules, term, permissions]);

	// El contador global mira TODOS los modulos, no solo los que sobreviven al
	// buscador: es el resumen del rol, no del filtro.
	const { totalMarcados, total } = useMemo(() => {
		let marcados = 0;
		let cuenta = 0;
		Object.values(modules).forEach((perms) => {
			perms.forEach((p) => {
				cuenta += 1;
				if (permissions[p.description]) {
					marcados += 1;
				}
			});
		});
		return { totalMarcados: marcados, total: cuenta };
	}, [modules, permissions]);

	// Al buscar se abren todos: si el usuario filtro por "eliminar", quiere ver
	// los resultados, no tener que desplegar modulo por modulo.
	const estaAbierto = (modName) => (term ? true : expanded === modName);

	const renderGrilla = (perms) =>
		perms.length === 0 ? null : (
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: '1fr',
						sm: 'repeat(auto-fill, minmax(230px, 1fr))'
					},
					gap: 0.5
				}}
			>
				{perms.map(({ description, label }) => (
					<FormControlLabel
						key={description}
						control={
							<Checkbox
								size="small"
								disabled={bloqueado}
								checked={!!permissions[description]}
								onChange={() => onToggle(description)}
							/>
						}
						label={<Typography fontSize={13.5}>{label}</Typography>}
						sx={{ mr: 0, minWidth: 0 }}
					/>
				))}
			</Box>
		);

	const alternarModulo = (mod) => {
		const marcar = mod.marcados < mod.total;
		const claves = mod.gobernables
			.filter((p) => !!permissions[p.description] !== marcar)
			.map((p) => p.description);
		if (claves.length > 0) {
			onToggleMany(claves, marcar);
		}
	};

	return (
		<Box>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', sm: 'center' }}
				justifyContent="space-between"
				sx={{ mb: 2 }}
			>
				<SearchField
					value={searchTerm}
					onChange={setSearchTerm}
					placeholder="Buscar permiso…"
					width={360}
				/>

				<Chip
					label={`${totalMarcados} de ${total} permisos`}
					size="small"
					variant="outlined"
					sx={{
						fontWeight: 'bold',
						color: totalMarcados > 0 ? 'primary.main' : 'text.disabled',
						borderColor: totalMarcados > 0 ? 'primary.main' : 'divider'
					}}
				/>
			</Stack>

			{parsed.length === 0 ? (
				<Typography textAlign="center" color="text.secondary" sx={{ py: 3 }}>
					{`Ningún permiso coincide con "${searchTerm}"`}
				</Typography>
			) : (
				parsed.map((mod) => (
					<Accordion
						key={mod.modName}
						expanded={estaAbierto(mod.modName)}
						onChange={() =>
							!term && setExpanded(estaAbierto(mod.modName) ? false : mod.modName)
						}
						disableGutters
						sx={{ mb: 1, '&:before': { display: 'none' } }}
					>
						<AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{ width: '100%', pr: 1 }}
							>
								{/* onClick detiene la propagacion para que marcar todo el
								    modulo no colapse el panel al mismo tiempo. */}
								<Checkbox
									size="small"
									disabled={bloqueado}
									checked={mod.marcados === mod.total}
									indeterminate={mod.marcados > 0 && mod.marcados < mod.total}
									onClick={(e) => e.stopPropagation()}
									onChange={() => alternarModulo(mod)}
									sx={{ p: 0.5 }}
								/>

								<Typography
									sx={{
										fontFamily: "'Poppins', sans-serif",
										fontWeight: 600,
										fontSize: 14,
										color: 'text.primary'
									}}
								>
									{mod.modName}
								</Typography>

								<Chip
									label={`${mod.marcados}/${mod.total}`}
									size="small"
									sx={{
										height: 20,
										fontSize: 11,
										fontWeight: 'bold',
										bgcolor: mod.marcados === 0 ? 'action.hover' : 'rgba(18,163,90,.13)',
										color: mod.marcados === 0 ? 'text.disabled' : 'primary.main'
									}}
								/>
							</Stack>
						</AccordionSummary>

						<AccordionDetails sx={{ pt: 0 }}>
							{renderGrilla(mod.operaciones)}

							{/* El subgrupo solo aparece si el modulo tiene formatos. */}
							{mod.impresion.length > 0 ? (
								<Box
									sx={{
										mt: mod.operaciones.length > 0 ? 2 : 0,
										pt: mod.operaciones.length > 0 ? 1.5 : 0,
										borderTop: mod.operaciones.length > 0 ? 1 : 0,
										borderColor: 'divider'
									}}
								>
									<Stack
										direction="row"
										alignItems="center"
										spacing={1}
										sx={{ mb: 0.5, ml: 1.5 }}
									>
										<PrintRoundedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
										<Typography
											fontSize={11.5}
											fontWeight="bold"
											color="text.secondary"
											sx={{ letterSpacing: '.05em' }}
										>
											IMPRESIÓN DE FORMATOS
										</Typography>
									</Stack>

									{renderGrilla(mod.impresion)}
								</Box>
							) : null}
						</AccordionDetails>
					</Accordion>
				))
			)}
		</Box>
	);
};

export default PermissionsPicker;
